/**
 * Exercise Form Component
 * Form for creating and editing exercises within a workshop
 */

import { useState } from "react";
import { Modal } from "./Modal";
import { MarkdownEditor } from "./MarkdownEditor";
import { Button } from "@components/common";
import type { Exercise, ContentType } from "@/types/models";
import type { ExerciseCreateRequest, ExerciseUpdateRequest } from "@/types/api";

// Content source options
type ContentSource = "file" | "inline";

const CONTENT_TYPE_OPTIONS: {
  value: ContentType;
  label: string;
  description: string;
}[] = [
  {
    value: "markdown",
    label: "Markdown",
    description: "Text-based content with markdown formatting",
  },
  {
    value: "jupyter",
    label: "Jupyter Notebook",
    description: "Interactive Python notebook",
  },
  {
    value: "interactive",
    label: "Interactive",
    description: "Custom interactive exercise",
  },
  {
    value: "presentation",
    label: "Presentation",
    description: "PDF slide deck for presentation viewing",
  },
];

interface FormState {
  title: string;
  description: string;
  content_type: ContentType;
  content_source: ContentSource;
  content_path: string;
  content_markdown: string;
  order_index: string;
  estimated_minutes: string;
}

const DEFAULT_FORM_STATE: FormState = {
  title: "",
  description: "",
  content_type: "markdown",
  content_source: "inline",
  content_path: "",
  content_markdown: "",
  order_index: "0",
  estimated_minutes: "",
};

function getInitialState(
  mode: "create" | "edit",
  exercise?: Exercise | null,
  defaultOrderIndex?: number,
): FormState {
  if (mode === "edit" && exercise) {
    // Determine content source based on which field has content
    const hasInlineContent = Boolean(exercise.content_markdown);
    const hasFilePath = Boolean(exercise.content_path);
    const contentSource: ContentSource = hasInlineContent
      ? "inline"
      : hasFilePath
        ? "file"
        : "inline";

    return {
      title: exercise.title,
      description: exercise.description || "",
      content_type: exercise.content_type,
      content_source: contentSource,
      content_path: exercise.content_path || "",
      content_markdown: exercise.content_markdown || "",
      order_index: exercise.order_index.toString(),
      estimated_minutes: exercise.estimated_minutes?.toString() || "",
    };
  }
  return {
    ...DEFAULT_FORM_STATE,
    order_index: (defaultOrderIndex ?? 0).toString(),
  };
}

interface ExerciseFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    data: ExerciseCreateRequest | ExerciseUpdateRequest,
  ) => Promise<void>;
  isLoading: boolean;
  exercise?: Exercise | null;
  workshopId: string;
  mode: "create" | "edit";
  defaultOrderIndex?: number;
}

export function ExerciseForm({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  exercise,
  workshopId,
  mode,
  defaultOrderIndex,
}: ExerciseFormProps) {
  // Form state initialized from props - parent should use key prop to reset
  const [form, setForm] = useState<FormState>(() =>
    getInitialState(mode, exercise, defaultOrderIndex),
  );

  // Helper to update individual form fields
  const updateField = <K extends keyof FormState>(
    field: K,
    value: FormState[K],
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Build content fields based on content source
    const contentFields =
      form.content_source === "inline"
        ? {
            content_markdown: form.content_markdown || undefined,
            content_path: undefined, // Clear file path when using inline
          }
        : {
            content_path: form.content_path || undefined,
            content_markdown: undefined, // Clear inline content when using file
          };

    if (mode === "create") {
      const data: ExerciseCreateRequest = {
        workshop_id: workshopId,
        title: form.title,
        description: form.description || undefined,
        content_type: form.content_type,
        ...contentFields,
        order_index: parseInt(form.order_index, 10) || 0,
        estimated_minutes: form.estimated_minutes
          ? parseInt(form.estimated_minutes, 10)
          : undefined,
      };
      await onSubmit(data);
    } else {
      const data: ExerciseUpdateRequest = {
        title: form.title,
        description: form.description || undefined,
        content_type: form.content_type,
        ...contentFields,
        order_index: parseInt(form.order_index, 10),
        estimated_minutes: form.estimated_minutes
          ? parseInt(form.estimated_minutes, 10)
          : undefined,
      };
      await onSubmit(data);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === "create" ? "Add Exercise" : "Edit Exercise"}
      description={
        mode === "create"
          ? "Add a new exercise to this workshop"
          : "Update exercise details"
      }
      size="md"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={isLoading}>
            {mode === "create" ? "Add Exercise" : "Save Changes"}
          </Button>
        </div>
      }
    >
      <form
        onSubmit={handleSubmit}
        className="space-y-4 max-h-[60vh] overflow-y-auto pr-2"
      >
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Title *
          </label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => updateField("title", e.target.value)}
            required
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="e.g., Building Your First Agent"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Description
          </label>
          <textarea
            value={form.description}
            onChange={(e) => updateField("description", e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="Brief description of this exercise..."
          />
        </div>

        {/* Content Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Content Type *
          </label>
          <div className="space-y-2">
            {CONTENT_TYPE_OPTIONS.map((option) => (
              <label
                key={option.value}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  form.content_type === option.value
                    ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
                    : "border-gray-300 dark:border-gray-600 hover:border-gray-400"
                }`}
              >
                <input
                  type="radio"
                  name="content_type"
                  value={option.value}
                  checked={form.content_type === option.value}
                  onChange={(e) =>
                    updateField("content_type", e.target.value as ContentType)
                  }
                  className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500"
                />
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {option.label}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {option.description}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Content Source Toggle - Only show for markdown content type */}
        {form.content_type === "markdown" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Content Source
            </label>
            <div className="flex gap-4">
              <label
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                  form.content_source === "inline"
                    ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
                    : "border-gray-300 dark:border-gray-600 hover:border-gray-400"
                }`}
              >
                <input
                  type="radio"
                  name="content_source"
                  value="inline"
                  checked={form.content_source === "inline"}
                  onChange={() => updateField("content_source", "inline")}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Write Content
                </span>
              </label>
              <label
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                  form.content_source === "file"
                    ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
                    : "border-gray-300 dark:border-gray-600 hover:border-gray-400"
                }`}
              >
                <input
                  type="radio"
                  name="content_source"
                  value="file"
                  checked={form.content_source === "file"}
                  onChange={() => updateField("content_source", "file")}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  File Reference
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Inline Markdown Editor - Show when inline mode and markdown type */}
        {form.content_type === "markdown" &&
          form.content_source === "inline" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Exercise Content
              </label>
              <MarkdownEditor
                content={form.content_markdown}
                onChange={(content) => updateField("content_markdown", content)}
                placeholder="Write your exercise content here..."
                minHeight="250px"
              />
              <p className="mt-1 text-xs text-gray-500">
                Use the toolbar to format text, add headings, lists, and code
                blocks.
              </p>
            </div>
          )}

        {/* Content Path - Show when file mode or non-markdown types */}
        {(form.content_source === "file" ||
          form.content_type !== "markdown") && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Content Path
            </label>
            <input
              type="text"
              value={form.content_path}
              onChange={(e) => updateField("content_path", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
              placeholder="e.g., exercises/01-intro.md"
            />
            <p className="mt-1 text-xs text-gray-500">
              Path to the exercise content file (relative to content directory).
            </p>
          </div>
        )}

        {/* Order and Duration Row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Order
            </label>
            <input
              type="number"
              value={form.order_index}
              onChange={(e) => updateField("order_index", e.target.value)}
              min="0"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className="mt-1 text-xs text-gray-500">
              Lower numbers appear first.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Duration (minutes)
            </label>
            <input
              type="number"
              value={form.estimated_minutes}
              onChange={(e) => updateField("estimated_minutes", e.target.value)}
              min="0"
              placeholder="e.g., 30"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className="mt-1 text-xs text-gray-500">
              Estimated time to complete.
            </p>
          </div>
        </div>
      </form>
    </Modal>
  );
}
